// src/features/Providers/page/MedicalProviderForm.tsx
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import DynamicForm, { type DynamicFormSchema } from '@/common/components/DynamicForm/DynamicForm';

export default function MedicalProviderForm() {

    const {  i18n } = useTranslation();
    const isArabic = i18n.language === 'ar';

    const medicalProviderFormSchema: DynamicFormSchema = {
        layout: 'vertical',
        showResetButton: true,

        sections: [
            {
                key: 'basic-info',
                title: 'Basic Information',
                type: 'card',
                layout: { type: 'grid', gutter: 24 },
                fields: [
                    {
                        name: "name",
                        label: "Name",
                        type: "input",
                        required: true,
                        pattern: "AlphanumericWithSpaceAndSymbols",
                        validation: [
                            { max: 255, message: "Name cannot exceed 255 characters." }
                        ],
                        colSpan: 12

                    },
                    //{
                    //    name: "nameAr",
                    //    label: "Name (Arabic)",
                    //    type: "input",
                    //    pattern: 'ArabicWithSpaceAndSymbols',
                    //    colSpan: 12,
                    //},
                    {
                        name: 'providerTypeId',
                        label: 'Provider Type',
                        type: 'select',
                        required: true,
                        options: [], // Should be dynamically fetched
                        colSpan: 12,
                        pattern: 'OnlyDigits',
                        dynamicOptions: {
                            url: "/Lookup/GetDropdownData",
                            method: "POST",
                            body: {
                                dropdowns: ["providerType"],
                            },
                            transformData: "(res) => res.providerType.map(item => ({ label: item.Name, value: item.Id }))",
                        },
                    },
                    {
                        name: 'hasPricing',
                        label: 'Has Pricing',
                        type: 'input',
                        hidden: true,
                    },
                ],
            },
            {
                key: 'contact-info',
                title: 'Contact & Address Information',
                type: 'card',
                layout: { type: 'grid', gutter: 24 },
                fields: [
                    {
                        name: 'countryId',
                        label: 'Country',
                        type: 'select',
                        colSpan: 12, // Full width
                        placeholder: "Select country",
                        required: true,
                        requiredMessage: "Country is required.",
                        showSearch: true, // Enable search functionality
                        options: [], // Will be filled dynamically
                        defaultValue: 104, // <-- Set Iraq as default
                        dynamicOptions: {
                            url: "/Lookup/GetDropdownData",
                            method: "POST",
                            body: { "dropdowns": ["country"] },
                            transformData: `(apiResponse) => apiResponse.country.map(item => ({ label: ${isArabic ? 'item.NameAr' : 'item.Name'}, value: item.Id }))`,

                        },
                    },
                    /*{ name: 'countryAr', label: 'Country (Arabic)', type: 'input', pattern: 'ArabicWithSpaceAndHyphen', colSpan: 12 },*/
                    {
                        name: 'cityId',
                        label: 'City',
                        type: 'select',
                        required: true,
                        requiredMessage: 'City is required.',
                        showSearch: true, // Enable search functionality
                        colSpan: 12,
                        options: [],
                        dynamicOptions: {
                            url: "/City/SearchByCountryId",
                            method: 'GET',
                            transformData: `(apiResponse) => apiResponse.map(item => ({ label: ${isArabic ? 'item.nameAr' : 'item.name'}, value: item.id }))`,
                            dependsOn: ['countryId'],
                        },
                        disabledWhen: [
                            {
                                field: 'countryId',
                                operator: 'isFalsy',
                            },
                        ],
                    }, 
                    {
                        name: 'currencyId',
                        label: 'Currency',
                        type: 'select',
                        required: true,
                        showSearch: true, // Enable search functionality
                        pattern: 'OnlyDigits',
                        colSpan: 12,
                        defaultValue: 38,
                        dynamicOptions: {
                            url: '/Lookup/GetDropdownData',
                            method: 'POST',
                            body: { dropdowns: ['currency'] },
                            transformData: '(res) => res.currency.map(item => ({ label: item.Name, value: item.Id, ISOCode: item.ISOCode }))',
                        },
                    },                 
                    {
                        name: 'state',
                        label: 'State',
                        type: 'input',
                        validation: [
                            { max: 100, message: 'State cannot exceed 100 characters.' }
                        ],
                        colSpan: 12,
                    },
                    //{
                    //    name: 'stateAr',
                    //    label: 'State (Arabic)',
                    //    type: 'input',
                    //    pattern: 'ArabicWithSpaceAndHyphen',
                    //    validation: [
                    //        { max: 100, message: 'State (Arabic) cannot exceed 100 characters.' }
                    //    ],
                    //    colSpan: 12,
                    //},
                    {
                        name: 'telephoneNo',
                        label: 'Telephone No.',
                        type: 'input',
                        required: true,
                        pattern: 'PhoneNumber',
                        colSpan: 12,
                    },
                    {
                        name: 'email',
                        label: 'Email',
                        type: 'input',
                        required: true,
                        pattern: 'Email',
                        validation: [
                            { max: 256, message: 'Email cannot exceed 256 characters.' }
                        ],
                        colSpan: 12,
                    },
                    {
                        name: 'address',
                        label: 'Address',
                        type: 'textarea',
                        pattern: 'Address',
                        validation: [
                            { max: 500, message: 'Address cannot exceed 500 characters.' }
                        ],
                        colSpan: 12,
                    },
                        //{
                        //    name: 'addressAr',
                        //    label: 'Address (Arabic)',
                        //    type: 'textarea',
                        //    pattern: 'ArabicLettersWithAddressSymbols',
                        //    validation: [
                        //        { max: 500, message: 'Address (Arabic) cannot exceed 500 characters.' }
                        //    ],
                        //    colSpan: 12,
                        //}
                    
                    
                ],
            },
            {
                key: 'other-info',
                title: 'Other Details',
                type: 'card',
                layout: { type: 'grid', gutter: 24 },
                fields: [
                 
                     {
                         name: 'ProfilePictureUpload', // Changed name to be unique
                         label: 'Upload Document',
                         type: 'fileUpload',
                         accept: 'image/*',
                         uploadUrl: '/MedicalProvider/upload-profile', // <--  valid endpoint
                         fileKey: 'profilePictureFile',
                         responseValueKey: 'data.path', 
                         maxCount: 1,
                         colSpan: 12,
                         updateFieldName: 'ProfilePicture',
                     },
                     {
                        name: 'ProfilePicture',
                        label: 'Profile Picture',
                        type: 'input',
                        hidden: true,
                        colSpan: 12,
                    
                     }
                   
                ],
            },
        ],
    };  

    return (
        <DynamicForm
            schema={medicalProviderFormSchema}
            submitApiUrl={API_ENDPOINTS.PROVIDERS.SAVE_MEDICALPROVIDER}
            getByIdApiUrl={API_ENDPOINTS.PROVIDERS.GET_MEDICALPROVIDER_BY_ID}
            updateApiUrl={API_ENDPOINTS.PROVIDERS.UPDATE_MEDICALPROVIDER}
            refreshTableDataKey={QUERY_KEYS.PROVIDERS.MEDICALPROVIDERS}
        />
    );
}
